import Page from 'assets/components/page/Page';
import ThemePageCentered from 'assets/components/page/themes/Theme.Page.Centered';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { ascInsuranceLogo } from 'assets/themes/assets/Images';
import Theme from 'assets/themes/Theme.Common';
import parseJwt from 'assets/utils/parsersAndValidation/Parsers';
import { HttpStatus, publicHomeRoute } from 'config/Api.Config';
import { getAuthToken } from 'config/Auth.Config';
import ContractConsentRequestType from 'models/enums/ContractConsentRequestType';
import HashedStringStore from 'models/hashedStringStore/Model.HashedStringStore';
import publicProcessContractConsentApi from 'models/public/processContractConsent/Api.ProcessContractConsent';
import { useEffect, useMemo, useRef, useState } from 'react';

const flexContainer: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

export const processContractConsentRoute = publicHomeRoute + '/processContractConsent';
export default function ProcessContractConsentIndex() {
  const { lang } = getLocales();
  const authToken = getAuthToken();
  const { query } = usePageRouter<
    undefined,
    {
      hs?: string;
      access_token?: string;
      contractConsentId?: number;
      contractId?: number;
      requestType?: string;
      isAccepted?: boolean | string;
    }
  >({
    route: processContractConsentRoute,
  });

  const {
    payload: hashedStringStore,
    status: hashedStringStatus,
    isExecuting: isFetchingHashedString,
  } = useApi(
    {
      action: HashedStringStore.getHashedString,
      body: {
        hash: decodeURIComponent(query?.hs),
        access_token: authToken,
      },
      wait: !authToken || !query?.hs,
    },
    [authToken, query?.hs]
  );

  // New links carry a hash (hs), legacy links still carry the access_token directly
  const accessToken = useMemo(
    () => (query?.hs ? hashedStringStore?.originalValue : query?.access_token),
    [query?.hs, query?.access_token, hashedStringStore?.originalValue]
  );
  const tokenUnpacked = useMemo(() => {
    if (accessToken)
      return parseJwt<Api.TokenUnpacked & { ContractId?: number; ContractConsentId: number }>(accessToken);
  }, [accessToken]);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [message, setMessage] = useState<string>(undefined);
  const [isError, setIsError] = useState<boolean>(false);
  const isProcessed = useRef(false);

  const showMessage = (text: string, error: boolean) => {
    setMessage(text);
    setIsError(error);
    setIsLoading(false);
  };

  // Hide the link params from the address bar; the router keeps its own copy, so query stays usable
  useEffect(() => {
    if (!window.location.search) return;
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.hash);
  }, []);

  useEffect(() => {
    if (!query?.hs || isFetchingHashedString || !hashedStringStatus) return;
    if (hashedStringStatus !== HttpStatus.ok || !hashedStringStore?.originalValue)
      showMessage(lang.consentLinkInvalidOrExpired, true);
  }, [hashedStringStatus, isFetchingHashedString]);

  useEffect(() => {
    if (!query?.hs && !query?.access_token) showMessage(lang.consentLinkInvalidOrExpired, true);
  }, [query?.hs, query?.access_token]);

  useEffect(() => {
    if (!tokenUnpacked || isProcessed.current) return;
    isProcessed.current = true;
    processConsent();
  }, [tokenUnpacked]);

  const processConsent = async () => {
    // Legacy tokens don't carry the consent claims, so those links still send the IDs in the query
    const contractConsentId = query?.hs ? tokenUnpacked?.ContractConsentId : query?.contractConsentId;
    const contractId = query?.hs ? tokenUnpacked?.ContractId : query?.contractId;
    if (!contractConsentId) return showMessage(lang.consentLinkInvalidOrExpired, true);
    if (tokenUnpacked.exp && tokenUnpacked.exp * 1000 <= Date.now())
      return showMessage(lang.consentResponseWindowExpired, true);

    const res = await publicProcessContractConsentApi({
      body: {
        contractConsentId: Number(contractConsentId),
        contractId: contractId ? Number(contractId) : null,
        requestType: query?.requestType || ContractConsentRequestType.EMAIL,
        isAccepted: String(query?.isAccepted).toLowerCase() === 'true',
      },
      access_token: accessToken,
    });

    if (res.status === HttpStatus.ok) showMessage(lang.thankYouForYourResponse, false);
    else if (res.status === HttpStatus.unauthorized) showMessage(lang.consentResponseWindowExpired, true);
    else showMessage(res.message || lang.consentResponseFailed, true);
  };

  return (
    <Page
      class={ThemePageCentered}
      style={{
        pageContent: {
          ...flexContainer,
          borderRadius: '10px',
          marginTop: 'auto',
          height: '50%',
        },
        pageWrapper: {
          ...flexContainer,
          height: '100%',
        },
      }}
      isLoading={isFetchingHashedString || isLoading}
      render={
        <div
          style={{
            ...flexContainer,
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            gap: '50px',
          }}
        >
          <div style={{ ...flexContainer, flexDirection: 'column', gap: '20px' }}>
            <img width={400} height="auto" src={ascInsuranceLogo} style={{ alignSelf: 'center' }} />
            <p style={{ fontSize: Theme.FontSize.L }}>{lang.welcomeToAutoshieldCanada}!</p>
          </div>
          <>
            <p>Dear customer,</p>
            <p
              style={{
                color: isError ? Theme.Color.error : Theme.Color.success,
              }}
            >
              {message}
            </p>
          </>
        </div>
      }
    />
  );
}
